<template>
  <div>
    <v-layout wrap justify-center mt-12>
      <v-flex xs12 sm9>
        <v-layout wrap>
          <v-flex xs12>
            <h1>{{ $t('a') }}</h1>
            {{user.data.email}}
          </v-flex>
        </v-layout>
        <v-layout wrap v-if="user.company.name">
          <v-flex xs12>
            <small>{{ $t('c1') }} {{user.company.name}}</small>
          </v-flex>
        </v-layout>
        <v-layout wrap v-if="['manager', 'admin'].includes(user.data.role)">
          <v-flex xs12>
            <small>{{ $t('y') }} {{ getRoleAsStr(user.data.role) }}</small>
            <v-tooltip right dark>
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ml-1" color="primary" small v-bind="attrs" v-on="on">mdi-information</v-icon>
              </template>
              <small v-html="$t('u2')"/>
            </v-tooltip>
          </v-flex>
        </v-layout>
        <v-layout wrap v-if="companyAdmins.length > 0">
          <v-flex xs12>
            <small>{{ $t('c') }} {{ companyAdmins.map(admin => admin.email).join(', ') }}.</small>
          </v-flex>
        </v-layout>
        <v-layout wrap>
          <v-flex xs6 sm4 md3>
            <locale-selector class="mt-6"/>
          </v-flex>
        </v-layout>
        <v-layout wrap v-if="user.company.isMaxNbOfDossiersPerUserEnabled && nbOfDossiersUsedByUserDuringCurrentBillingPeriod">
          <v-flex xs12>
            <small>
              <div>
                <span>{{ nbOfDossiersUsedByUserDuringCurrentBillingPeriod }} </span>
                <span v-if="nbOfDossiersUsedByUserDuringCurrentBillingPeriod >= 2">{{ $t('y8plural') }}</span>
                <span v-else>{{ $t('y8') }}</span>
              </div>
              <div>{{ $t('y9') }} {{ user.company.maxNbOfDossiersPerUser }} {{ $t('y10') }}</div>
              <div>{{ $t('y11') }} {{ (new Date(user.company.currentMonthlyPeriodDates.endDate * 1000)).toLocaleDateString() }}.</div>
            </small>
          </v-flex>
        </v-layout>

        <v-layout wrap justify-center mt-12 mb-12 v-if="user.data.role === 'admin' && user.company.hasOrHadSubscription">
          <v-flex xs8 mb-12 mt-8>
            <v-card class="px-8 pt-3 pb-8">
              <v-layout wrap pt-4>
                <v-flex xs6>
                  <h2>{{ $t('1') }}</h2>
                </v-flex>
                <v-flex xs6 text-end>
                  <v-chip small color="primary" class="mt-1">
                    {{ $t('onlyVisibleByAdmins') }}
                  </v-chip>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs12>
                  <small>
                    <div v-if="['active', 'trialing'].includes(user.company.subscriptionStatus)">
                      <div v-if="user.company.currentMonthlyPeriodDates">{{ $t('3') }} {{ user.company.dossiersLeftNb }} {{ $t('4') }} {{ currentPeriodEnd }} {{ $t('4bis') }}</div>
                      <div v-if="user.company.parentCompanyId && user.company.maxNbOfDossiers" class="mt-3">{{ $t('4ter') }} {{ user.company.maxNbOfDossiers }} {{ $t('4q') }}</div>
                    </div>
                    <div v-else>
                      <div class="orange--text" v-if="user.company.subscriptionStatus === 'canceled'">
                        <div><b>{{ $t('canceled1') }}</b></div>
                      </div>
                      <div class="orange--text" v-else>
                        <div><b>{{ $t('disabled1') }}</b></div>
                        <div>{{ $t('disabled2') }}</div>
                      </div>
                    </div>
                  </small>
                </v-flex>
              </v-layout>
              <v-layout wrap justify-space-between>
                <v-flex xs12 v-if="user.company.parentCompanyId">
                  <div v-if="user.data.isParentCompanyAdmin">
                    <v-layout wrap mt-5>
                      <v-flex xs12>
                        <v-divider/>
                      </v-flex>
                      <v-flex xs12 text-end mt-4>
                        <v-chip small color="primary" class="text-end">
                          {{ $t('onlyVisibleByParentCompanyAdmins') }}
                        </v-chip>
                      </v-flex>
                      <v-flex xs12 text-center mt-3>
                        <v-btn outlined to="/parent-company-admin-dashboard">{{ $t('b') }}</v-btn>
                      </v-flex>
                    </v-layout>
                  </div>
                  <div v-else class="mt-3">
                    <small v-if="companyAdmins.filter(admin => admin.isParentCompanyAdmin).length > 0">
                      {{ $t('parentCompanyNoBillingDisplayed') }} {{ companyAdmins.filter(admin => admin.isParentCompanyAdmin).map(admin => admin.email).join(', ') }}.
                    </small>
                  </div>
                </v-flex>
                <v-flex mt-5 xs12 text-center v-else>
                  <div v-if="user.company.subscriptionStatus === 'canceled'">
                    <v-btn to="/subscribe" outlined class="outlinedButtonWhiteBackground">{{ $t('canceled3') }}</v-btn>
                  </div>
                  <div v-else-if="user.company.subscriptionStatus === 'active'">
                    <v-btn to="/edit-subscription" outlined class="outlinedButtonWhiteBackground">{{ $t('d') }}</v-btn>
                  </div>
                  <div class="mt-4">
                    <small>
                      <v-btn v-if="stripeBasicCustomerPortalUrl" :href="stripeBasicCustomerPortalUrl" target="_blank" outlined class="outlinedButtonWhiteBackground">{{ $t('d2') }}</v-btn>
                    </small>
                  </div>
                  <v-progress-linear v-if="isInitStripeBasicCustomerPortalSessionPending" class="mt-6" indeterminate color="primary"></v-progress-linear>
                </v-flex>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12 sm9>
        <v-layout wrap justify-center mb-12 v-if="user.data.role === 'admin'">
          <v-flex xs8 mb-12>
            <company-labels/>
          </v-flex>
        </v-layout>

        <v-layout wrap justify-center mt-12 mb-12>
          <v-flex xs8 mb-12>
            <templates-preferences/>
          </v-flex>
        </v-layout>

        <v-layout wrap justify-center mt-12 mb-12>
          <v-flex xs8>
            <account-personal-data/>
          </v-flex>
        </v-layout>

        <v-layout wrap justify-center mt-12>
          <v-flex xs8 mt-12>
            <account-change-password/>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12 px-2 mt-12>
        <v-layout wrap justify-center mt-12 mb-12 v-if="['manager', 'admin'].includes(user.data.role)">
          <v-flex xs12>
            <v-card class="px-8 pt-3 pb-3">
              <v-layout wrap pt-4>
                <v-flex xs12>
                  <h2>{{ $t('7') }}</h2>
                </v-flex>
              </v-layout>
              
              <v-layout wrap mt-6>
                <v-flex xs6>
                  <h3>{{ $t('z1') }}</h3>
                </v-flex>
                <v-flex xs6 text-end>
                  <v-chip small color="primary" class="mt-1">
                    {{ $t('onlyVisibleByAdminsAndManagers') }}
                  </v-chip>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs12>
                  <small>
                    <div v-if="isCompanyDataAsAdminFetched">
                      <div class="mb-3">
                        <span>{{ $t('8') }} <b>{{ user.company.name }}</b> {{ $t('8bis') }} {{ companyUsers.length }} </span>
                        <span v-if="companyUsers.length >= 2">{{ $t('9plural') }}</span>
                        <span v-else>{{ $t('9') }}</span>
                      </div>

                      <company-users-table
                        :company="user.company"
                        :companyUsers="companyUsers"
                        :isEditUserRoleEnabled="true"
                        @showEditUserRole="showEditUserRole"
                      />

                    </div>
                    <div v-else>
                      <v-progress-linear indeterminate color="primary" class="mt-4"></v-progress-linear>
                    </div>
                  </small>
                </v-flex>
              </v-layout>

              <v-layout wrap v-if="['admin'].includes(user.data.role) && user.company.isMaxNbOfDossiersPerUserDisplayed">
                <v-flex xs12>
                  <v-layout wrap>
                    <v-flex xs12>
                      <v-layout wrap justify-center>
                        <v-flex xs10>
                          <v-divider class="mt-8 mb-8"/>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex>
                      <h3>{{ $t('y1') }}</h3>
                    </v-flex>
                    <v-flex text-end>
                      <v-chip small color="primary" class="mt-1">
                        {{ $t('onlyVisibleByAdmins') }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                  <div v-if="isCompanyDataAsAdminFetched">
                    <v-layout wrap>
                      <v-flex xs12>
                        <v-switch
                          v-model="isMaxNbOfDossiersPerUserEnabled"
                          color="secondary"
                          inset
                          :label="`${ $t('y2') } ${ isMaxNbOfDossiersPerUserEnabled ? $t('y3') : $t('y4') }`"
                          :disabled="!Boolean(user.company.currentMonthlyPeriodDates.startDate)"
                        />
                        <div v-if="!user.company.currentMonthlyPeriodDates.startDate">
                          <small class="orange--text"><b>{{ $t('y7') }}</b></small>
                        </div>
                      </v-flex>
                    </v-layout>
                    <v-layout wrap v-if="isMaxNbOfDossiersPerUserEnabled">
                      <v-flex xs12>
                          <small>
                            <b>{{ $t('y5') }}</b>
                          </small>
                          <v-text-field v-model="maxNbOfDossiersPerUser" :suffix="$t('y6')" type="number" :min="1" :step="1" dense outlined  style="width:300px" />
                      </v-flex>
                    </v-layout>
                  </div>
                  <div v-else>
                    <v-progress-linear indeterminate color="primary" class="mt-4"></v-progress-linear>
                  </div>
                </v-flex>
              </v-layout>

              <v-layout wrap v-if="['admin'].includes(user.data.role)">
                <v-flex xs12>
                  <v-layout wrap>
                    <v-flex xs12>
                      <v-layout wrap justify-center>
                        <v-flex xs10>
                          <v-divider class="mt-8 mb-8"/>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex xs6>
                      <h3>{{ $t('z2') }}</h3>
                    </v-flex>
                    <v-flex xs6 text-end>
                      <v-chip small color="primary" class="mt-1">
                        {{ $t('onlyVisibleByAdmins') }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                  <v-layout wrap>
                    <v-flex xs12>
                      <small>
                        <div class="mt-2" v-if="user.company.emailDomains.length > 0">
                          <div>
                            <span v-if="user.company.emailDomains.length === 1">{{ $t('10') }} </span>
                            <span v-else>{{ $t('10plural') }} </span>
                            <b>{{ user.company.emailDomains.map(x => `@${x}`).join(' ') }}</b>
                          </div>
                          <div>{{ $t('z3') }}</div>
                        </div>
                        <div v-if="companyDefaultNewUserRole !== null" class="mt-10">
                          <v-layout wrap>
                            <v-flex xs6 sm4>
                              <v-select v-model="companyDefaultNewUserRole" :label="$t('defaultNewUserRole1')" :items="allRoles" item-text="name" item-value="value" outlined dense></v-select>
                            </v-flex>
                            <v-flex xs6 sm8 pt-2 pl-3>
                              <v-tooltip right dark>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-icon class="ml-1" color="primary" small v-bind="attrs" v-on="on">mdi-information</v-icon>
                                </template>
                                <small v-html="$t('u2')"/>
                              </v-tooltip>
                            </v-flex>
                          </v-layout>
                        </div>
                        <div v-if="companyIsUserValidationNecessary !== null">
                          <div>
                            <v-switch
                              v-model="companyIsUserValidationNecessary"
                              color="secondary" inset
                              :label="`${$t('z4')} - ${companyIsUserValidationNecessary ? $t('z5') : $t('z6')}`"
                              hide-details
                              class="mb-2"
                            />
                          </div>
                          <div class="ml-12 pl-2">
                            <div v-if="companyIsUserValidationNecessary">
                              <div>{{ $t('z7') }}</div>
                            </div>
                            <div v-else>
                              <span v-if="user.company.emailDomains.length === 1">{{ $t('11') }} </span>
                              <span v-else>{{ $t('11plural') }} </span>
                            </div>
                          </div>
                        </div>
                      </small>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-layout justify-end wrap v-if="companyIsUserValidationNecessary !== null && companyIsUserValidationNecessary && ['manager', 'admin'].includes(user.data.role)">
                <v-flex xs12 sm11>
                  <v-layout wrap>
                    <v-flex xs12>
                      <v-layout wrap justify-center>
                        <v-flex xs12>
                          <v-divider class="mt-8 mb-8"/>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex xs6>
                      <h3>{{ $t('z8') }}</h3>
                    </v-flex>
                    <v-flex xs6 text-end>
                      <v-chip small color="primary" class="mt-1">
                        {{ $t('onlyVisibleByAdminsAndManagers') }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                  <v-layout wrap v-if="isAcceptingOrRefusingAUserValidationRequest" justify-center>
                    <v-flex xs6>
                      <v-progress-linear indeterminate color="primary" class="mt-6"/>
                    </v-flex>
                  </v-layout>
                  <v-layout wrap v-else>
                    <v-flex xs12>
                      <small>
                        <div v-if="userValidationRequests.length === 0">
                          <v-icon small class="mr-1">mdi-check</v-icon>
                          <span>{{ $t('z9') }}</span>
                        </div>
                        <div v-else>{{ $t('z10') }}</div>
                        <v-layout wrap>
                          <v-flex xs12 v-for="(userValidationRequest, key) in userValidationRequests" :key="key">
                            <v-card class="px-6 py-2 my-2" :elevation="0" style="border: 1px solid grey">
                              <v-layout wrap>
                                <v-flex xs4 pt-1>
                                  <b>{{ userValidationRequest.userToBeValidatedEmail }}</b>
                                </v-flex>
                                <v-flex xs3 pt-1>
                                  {{ $t('z11') }} {{ new Date(userValidationRequest.createdAt).toLocaleDateString() }}.
                                </v-flex>
                                <v-flex xs5>
                                  <v-btn small class="secondary mr-12" @click="acceptOrRefuseUserValidationRequest(userValidationRequest.id, true)">{{ $t('z12') }}</v-btn>
                                  <v-btn x-small outlined @click="acceptOrRefuseUserValidationRequest(userValidationRequest.id, false)">{{ $t('z13') }}</v-btn>
                                </v-flex>
                              </v-layout>
                            </v-card>
                          </v-flex>
                        </v-layout>
                      </small>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>

              <v-layout wrap justify-end v-if="companyIsUserValidationNecessary !== null && companyIsUserValidationNecessary && ['manager', 'admin'].includes(user.data.role)">
                <v-flex xs12 sm11>
                  <v-layout wrap>
                    <v-flex xs12>
                      <v-layout wrap justify-center>
                        <v-flex xs12>
                          <v-divider class="mt-8 mb-8"/>
                        </v-flex>
                      </v-layout>
                    </v-flex>
                    <v-flex xs6>
                      <h3>{{ $t('z14') }}</h3>
                    </v-flex>
                    <v-flex xs6 text-end>
                      <v-chip small color="primary" class="mt-1">
                        {{ $t('onlyVisibleByAdminsAndManagers') }}
                      </v-chip>
                    </v-flex>
                  </v-layout>
                  <v-layout wrap v-if="isCancelingAnInvitation" justify-center>
                    <v-flex xs6>
                      <v-progress-linear indeterminate color="primary" class="mt-6"/>
                    </v-flex>
                  </v-layout>
                  <v-layout wrap v-else>
                    <v-flex xs12>
                      <small>
                        <div v-if="userValidationInvitations.length === 0">
                          <span>{{ $t('z15') }}</span>
                        </div>
                        <div v-else>{{ $t('z16') }}</div>
                        <v-layout wrap>
                          <v-flex xs12 v-for="(userValidationInvitation, key) in userValidationInvitations" :key="key">
                            <v-card class="px-6 py-2 my-2" :elevation="0" style="border: 1px solid grey">
                              <v-layout wrap>
                                <v-flex xs4>
                                  <b>{{ userValidationInvitation.userToBeValidatedEmail }}</b>
                                </v-flex>
                                <v-flex xs3>
                                  {{ $t('z17') }} {{ new Date(userValidationInvitation.createdAt).toLocaleDateString() }}.
                                </v-flex>
                                <v-flex xs5>
                                  <v-btn x-small outlined @click="cancelUserInvitation(userValidationInvitation.id)">{{ $t('z18') }}</v-btn>
                                </v-flex>
                              </v-layout>
                            </v-card>
                          </v-flex>
                        </v-layout>
                      </small>
                    </v-flex>
                    <v-flex xs12 mt-3>
                      <invite-user-to-join-team />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>


              <v-layout wrap>
                <v-flex xs12 text-center>
                  <v-layout wrap justify-center>
                    <v-flex xs10>
                      <v-divider class="mt-8 mb-8"/>
                    </v-flex>
                  </v-layout>
                  <div class="mb-3">
                    <small>
                      {{ $t('12') }} <a href="mailto:martin@esnwebservices.com">martin@esnwebservices.com</a>
                    </small>
                  </div>
                </v-flex>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex xs12 text-center mt-12>
        <div><img :src="images.bottom" height="175px" class="mt-12 mb-12"></div>
      </v-flex>

    </v-layout>

    <v-dialog v-model="isShowingEditUserRoleDialog" width="40%" :persistent="isUpdatingRoleStatus">
      <v-card class="px-6 pb-6 pt-3" v-if="itemWhoseUserRoleIsBeingEdited">
        <div v-if="!isUpdatingRoleStatus">
          <v-layout wrap>
            <v-flex xs12 text-end>
              <v-chip small color="primary" class="mb-2">
                {{ $t('onlyVisibleByAdmins') }}
              </v-chip>
            </v-flex>
            <v-flex xs12 text-center>
              <h3>{{ $t('u1') }}</h3>
            </v-flex>
            <v-flex xs12 my-3>
              <small v-html="$t('u2')"/>
            </v-flex>
            <v-flex xs12 mt-2>
              <small>
                <div>{{ [itemWhoseUserRoleIsBeingEdited.firstName, itemWhoseUserRoleIsBeingEdited.lastName].filter(Boolean).join(' ') }}</div>
                <div>{{ itemWhoseUserRoleIsBeingEdited.email }}</div>
                <div>{{ $t('u2bis') }} {{ getRoleAsStr(itemWhoseUserRoleIsBeingEdited.role) }}</div>
              </small>
              <v-select v-model="newUserRole" :label="$t('u2ter')" :items="allRoles" item-text="name" item-value="value" outlined dense class="mt-6"></v-select>
              <div v-if="newUserRole === 'disabled'" class="orange--text mb-3">
                <small>
                  <div>{{ $t('disableUser1') }}</div>
                  <div>{{ $t('disableUser2') }}</div>
                </small>
              </div>
            </v-flex>
          </v-layout>
          <v-layout wrap mt-3>
            <v-flex xs6 text-center>
              <v-btn class="primary" @click="updateRole" :disabled="!newUserRole">{{ $t('u3') }}</v-btn>
            </v-flex>
            <v-flex xs6 text-center>
              <v-btn outlined @click="isShowingEditUserRoleDialog = false">{{ $t('u4') }}</v-btn>
            </v-flex>
          </v-layout>
        </div>
        <div v-if="isUpdatingRoleStatus">
          <v-layout wrap>
            <v-flex xs12 text-center>
              <div class="mb-2"><b>{{ $t('u5') }}</b></div>
              <v-progress-linear indeterminate color="primary"></v-progress-linear> 
            </v-flex>
          </v-layout>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AccountPersonalData from '@/components/AccountPersonalData'
import AccountChangePassword from '@/components/AccountChangePassword'
import CompanyLabels from '@/components/CompanyLabels'
import TemplatesPreferences from '@/components/TemplatesPreferences'
import InviteUserToJoinTeam from '@/components/InviteUserToJoinTeam'

import LocaleSelector from '@/components/LocaleSelector'
import CompanyUsersTable from '@/components/CompanyUsersTable.vue';

export default {
  components: {
    AccountPersonalData,
    AccountChangePassword,
    LocaleSelector,
    CompanyLabels,
    TemplatesPreferences,
    InviteUserToJoinTeam,
    CompanyUsersTable
  },
  data: () => ({
    stripeBasicCustomerPortalUrl: null,
    images: {
      bottom: require('@/assets/app/images/summer_landscape_flatline.svg')
    },
    isInitStripeBasicCustomerPortalSessionPending: true,
    isCompanyDataAsAdminFetched: false,
    companyUsers: [],
    companyUsersHeaders: [],
    itemWhoseUserRoleIsBeingEdited: null,
    isUpdatingRoleStatus: false,
    isShowingEditUserRoleDialog: false,
    newUserRole: null,
    allRoles: [],
    companyIsUserValidationNecessary: null,
    companyDefaultNewUserRole: null,
    userValidations: [],
    isAcceptingOrRefusingAUserValidationRequest: false,
    stopUserValidationsListener: null,
    companyAdmins: [],
    isMaxNbOfDossiersPerUserEnabled: null,
    maxNbOfDossiersPerUser: null,
    nbOfDossiersUsedByUserDuringCurrentBillingPeriod: null,
    isCancelingAnInvitation: false,
  }),
  async mounted() {
    this.allRoles = [
      { name: this.$t('role1'), value: 'normal' },
      { name: this.$t('role2'), value: 'manager' },
      { name: this.$t('role3'), value: 'admin' },
      { name: this.$t('role4'), value: 'disabled' },
    ]
    await this.waitForCompanyToBeLoaded()
    this.initStripeBasicCustomerPortalSession()
    this.getUserValidationsAndListenFor()
    this.getCompanyAdmins()
    this.getNbOfDossiersUsedByUserDuringCurrentBillingPeriod()
    await this.getCompanyDataAsAdminOrManager()
    
    
    this.companyIsUserValidationNecessary = this.user.company.isUserValidationNecessary
    this.companyDefaultNewUserRole = this.user.company.defaultNewUserRole

    this.isMaxNbOfDossiersPerUserEnabled = this.user.company.isMaxNbOfDossiersPerUserEnabled
    this.maxNbOfDossiersPerUser = this.user.company.maxNbOfDossiersPerUser
  },
  beforeDestroy() {
    if (this.stopUserValidationsListener) this.stopUserValidationsListener()
  },
  computed: {
    ...mapGetters({
      user: "user"
    }),
    companyHasOrHadSubscription() {
      return this.user.company && this.user.company.hasOrHadSubscription
    },
    currentPeriodEnd() {
      return new Date(this.user.company.currentMonthlyPeriodDates.endDate * 1000).toLocaleDateString() //toLocaleString()
    },
    userValidationRequests() {
      return this.userValidations.filter(userValidation => userValidation.type === 'request').sort((a, b) => b.createdAt < a.createdAt ? 1 : -1)
    },
    userValidationInvitations() {
      return this.userValidations.filter(userValidation => userValidation.type === 'invitation').sort((a, b) => b.createdAt < a.createdAt ? 1 : -1)
    },
  },
  methods: {
    async waitForCompanyToBeLoaded() {
      const user = this.user
      if (!user.data || (user.data.companyId && user.company === null)) {
        await this.sleep(100);
        await this.waitForCompanyToBeLoaded()
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async getNbOfDossiersUsedByUserDuringCurrentBillingPeriod() {
      if (this.user.company.currentMonthlyPeriodDates.startDate) {
        const { nbOfDossiersUsedByUserDuringCurrentBillingPeriod } = (await this.$functions.httpsCallable('getNbOfDossiersUsedByUserDuringCurrentBillingPeriod')()).data
        this.nbOfDossiersUsedByUserDuringCurrentBillingPeriod = nbOfDossiersUsedByUserDuringCurrentBillingPeriod
      }
    },
    async initStripeBasicCustomerPortalSession() {
      if (this.user.data.role === 'admin' && this.companyHasOrHadSubscription && !this.user.company.parentCompanyId) {
        const stripeSessionReponse = await this.$functions.httpsCallable('createStripeCustomerPortalSession')({
          mode: 'basic',
          returnUrl: window.location.origin + '/account'
        })
        this.stripeBasicCustomerPortalUrl = stripeSessionReponse.data.url
        this.isInitStripeBasicCustomerPortalSessionPending = false
      }
    },
    async getCompanyDataAsAdminOrManager() {
      if (['manager', 'admin'].includes(this.user.data.role)) {
        const companyDataAsAdmin = (await this.$functions.httpsCallable('getCompanyDataAsAdminOrManager')()).data
        this.companyUsers = companyDataAsAdmin.companyUsers
        this.isCompanyDataAsAdminFetched = true
      }
    },
    getRoleAsStr(role) {
      return {
        normal: this.$t('role1'),
        manager: this.$t('role2'),
        admin: this.$t('role3'),
        disabled: this.$t('role4')
      }[role]
    },
    showEditUserRole(item) {
      this.isShowingEditUserRoleDialog = true
      this.itemWhoseUserRoleIsBeingEdited = item
    },
    async updateRole() {
      this.isUpdatingRoleStatus = true
      await this.$functions.httpsCallable('updateUserRole')({
        userId: this.itemWhoseUserRoleIsBeingEdited.userId,
        role: this.newUserRole
      })
      this.itemWhoseUserRoleIsBeingEdited = null
      this.newUserRole = null
      this.isUpdatingRoleStatus = false
      this.isShowingEditUserRoleDialog = false
      // Refresh table
      this.isCompanyDataAsAdminFetched = false
      await this.getCompanyDataAsAdminOrManager()
    },
    getUserValidationsAndListenFor() {
      if (['manager', 'admin'].includes(this.user.data.role)) {
        this.stopUserValidationsListener = this.$db.collection("userValidations").where("companyId", "==", this.user.data.companyId).onSnapshot((querySnapshot) => {
          const userValidations = [];
          querySnapshot.forEach((userValidationSnap) => {
            const userValidation = userValidationSnap.data()
            userValidation.id = userValidationSnap.id
            userValidations.push(userValidation)
          });
          this.userValidations = userValidations
        });
      }
    },
    async acceptOrRefuseUserValidationRequest(userValidationId, isAccepting) {
      this.isAcceptingOrRefusingAUserValidationRequest = true
      await this.$functions.httpsCallable('acceptOrRefuseUserValidationRequest')({
        userValidationId,
        isAccepting
      })
      setTimeout(() => {
        this.isAcceptingOrRefusingAUserValidationRequest = false
      }, 1000)
    },
    async cancelUserInvitation(userValidationId) {
      this.isCancelingAnInvitation = true
      await this.$functions.httpsCallable('cancelUserInvitationToJoinTeam')({
        userValidationId
      })
      this.isCancelingAnInvitation = false
    },
    async getCompanyAdmins() {
      this.companyAdmins = (await this.$functions.httpsCallable('getAdminsOfMyCompany')()).data.admins
    },
    async updateCompanyMaxNbOfDossiersPerUser() {
      if (
        this.user.company.isMaxNbOfDossiersPerUserDisplayed
        && (
          this.isMaxNbOfDossiersPerUserEnabled !== this.user.company.isMaxNbOfDossiersPerUserEnabled
          || this.maxNbOfDossiersPerUser !== this.user.company.maxNbOfDossiersPerUser
        )
      )
      await this.$functions.httpsCallable('updateCompanyMaxNbOfDossiersPerUser')({
        isMaxNbOfDossiersPerUserEnabled: this.isMaxNbOfDossiersPerUserEnabled,
        maxNbOfDossiersPerUser: this.maxNbOfDossiersPerUser
      })
    }
  },
  watch: {
    companyIsUserValidationNecessary: {
      async handler(val, oldVal) {
        if (oldVal !== null) {
          await this.$functions.httpsCallable('updateCompanyIsUserValidationNecessary')({
            isUserValidationNecessary: val
          })
        }
      }
    },
    companyDefaultNewUserRole: {
      async handler(val, oldVal) {
        if (oldVal !== null) {
          await this.$functions.httpsCallable('updateCompanyDefaultNewUserRole')({
            defaultNewUserRole: val
          })
        }
      }
    },
    isMaxNbOfDossiersPerUserEnabled: {
      async handler(val, oldVal) {
        if (oldVal !== null) {
          this.updateCompanyMaxNbOfDossiersPerUser()
        }
      }
    },
    maxNbOfDossiersPerUser: {
      async handler(val, oldVal) {
        if (oldVal !== null) {
          if (!val) return;
          if (val && typeof val !== 'number') return this.maxNbOfDossiersPerUser = parseInt(this.maxNbOfDossiersPerUser)
          if (val < 1) return this.maxNbOfDossiersPerUser = 1
          this.updateCompanyMaxNbOfDossiersPerUser() 
        }
      }
    },
    isShowingEditUserRoleDialog: {
      handler(val) {
        if (!val) {
          this.newUserRole = null
        }
      }
    }
  }
}
</script>


<i18n>
{
  "fr": {
    "a": "Votre compte",
    "b": "Espace administrateur du groupe",
    "c1": "Votre équipe :",
    "c": "Administrateurs de votre équipe :",
    "d": "Gérer votre abonnement",
    "d2": "Voir vos factures / Changer le mode de paiement / Informations de facturation",
    "disabled1": "Abonnement désactivé",
    "disabled2": "Le paiement de votre dernière facture n'a pas été réalisé.",
    "disabled3": "Gérer les paiements",
    "canceled1": "Abonnement annulé",
    "canceled2": "",
    "canceled3": "Reprendre un abonnement",
    "1": "Votre abonnement",
    "2": "Vous êtes administrateur de votre équipe.",
    "3": "Il reste",
    "4": "dossiers à votre équipe, jusqu'au",
    "4bis": "dans la journée.",
    "4ter": "Plafond mensuel de dossiers de votre équipe : ",
    "4q": "(déterminé par l'administrateur de votre groupe).",
    "5": "Votre formule actuelle :",
    "6": "Voir toutes les formules",
    "7": "Votre équipe",
    "8": "Votre équipe",
    "8bis": "a",
    "9": "utilisateur.",
    "9plural": "utilisateurs.",
    "10": "Votre équipe est liée au nom de domaine",
    "11": "Toute personne créant un compte avec une adresse email ayant votre nom de domaine pourra choisir de s'associer à votre équipe.",
    "10plural": "Votre équipe est liée à ces noms de domaine",
    "11plural": "Toute personne créant un compte avec une adresse email ayant un de ces noms de domaine pourra choisir de s'associer à votre équipe.",
    "12": "Pour toute demande concernant la gestion de votre équipe, contactez-nous à",
    "13": "Aucun",
    "14": "Solo",
    "15": "Standard",
    "16": "Premium",
    "17": "Solo Annuel",
    "18": "Standard Annuel",
    "19": "Premium Annuel",
    "onlyVisibleByAdmins": "Uniquement visible par les admins",
    "onlyVisibleByAdminsAndManagers": "Visible par les admins et les managers",
    "onlyVisibleByParentCompanyAdmins": "Uniquement visible par les admins du groupe",
    "usersDataTable1": "Email",
    "usersDataTable2": "Prénom",
    "usersDataTable3": "Nom",
    "usersDataTable5": "Dossiers créés sur les 30 derniers jours",
    "usersDataTable5bis": "Dossiers créés depuis le début du cycle mensuel actuel",
    "usersDataTable6": "Date de création de compte",
    "usersDataTable7": "Tous",
    "usersDataTable8": "Nombre d'utilisateurs par page",
    "usersDataTable9": "{0}-{1} sur {2}",
    "usersDataTable10": "Rôle",
    "role1": "Normal",
    "role2": "Manager",
    "role3": "Admin",
    "role4": "Désactivé",

    "u1": "Éditer le rôle d'un utilisateur",
    "u2": "• Les Admins peuvent gérer l'abonnement de l'équipe.<br>• Les Admins peuvent nommer des Admins et des Managers.<br>• Les Admins et les Managers peuvent inviter/accepter/refuser des utilisateurs normaux dans l'équipe.",
    "u2bis": "Rôle actuel :",
    "u2ter": "Nouveau rôle",
    "u3": "Sauvegarder",
    "u4": "Annuler",
    "u5": "Sauvegarde en cours...",

    "y": "Votre rôle :",

    "z1": "Membres de votre équipe",
    "z2": "Paramètres pour rejoindre l'équipe",
    "z3": "Les nouveaux utilisateurs doivent avoir une adresse email avec un nom de domaine lié à votre équipe.",
    "z4": "Validation nécessaire des nouveaux utilisateurs",
    "z5": "Activée",
    "z6": "Désactivée",
    "z7": "Les nouveaux utilisateurs devront demander la validation de leur compte par un Manager ou un Admin de votre équipe.",
    "z8": "Demandes de validation en attente",
    "z9": "Aucun utilisateur n'attend la validation de son compte.",
    "z10": "Ces utilisateurs attendent la validation de leurs comptes pour rejoindre votre équipe.",
    "z11": "Demandée le",
    "z12": "Accepter",
    "z13": "Refuser",
    "z14": "Adresses email de collaborateurs pré-validées",
    "z15": "Aucune adresse email pré-validée.",
    "z16": "Ces utilisateurs whitelistés par les Managers et Admins peuvent rejoindre votre équipe en se créant un compte avec cette adresse email.",
    "z17": "Pré-validée le",
    "z18": "Annuler",
    
    "y1": "Limiter le nombre de dossiers consommables mensuellement par chaque utilisateur",
    "y2": "Limite",
    "y3": "activée",
    "y4": "désactivée",
    "y5": "Nombre maximal :",
    "y6": "dossiers / utilisateur / mois",
    "y7": "Vous pourrez activer cette option lorsque vous aurez un abonnement actif.",
    "y8": "dossier consommé par votre compte personnel depuis le début du cycle mensuel.",
    "y8plural": "dossiers consommés par votre compte personnel depuis le début du cycle mensuel.",
    "y9": "Votre équipe a mis en place un plafond à",
    "y10": "dossiers par utilisateur par mois.",
    "y11": "Prochain renouvellement le",

    "parentCompanyNoBillingDisplayed": "Pour mettre à jour l'abonnement, contacter les administrateurs responsables de l'abonnement de votre équipe :",
    "defaultNewUserRole1": "Rôle par défaut des nouveaux utilisateurs",

    "disableUser1": "Cet utilisateur ne pourra plus se connecter à son compte ni accéder aux données de votre équipe.",
    "disableUser2": "Les dossiers créés par cet utilisateur seront conservés et garderont leur visibilité auprès des membres de l'équipe."
  },
  "en": {
    "a": "Your account",
    "b": "Go to your group admin space",
    "c1": "Your team :",
    "c": "Your team administrators :",
    "d": "Manage subscription",
    "d2": "View invoices / Change payment method / Change billing information",
    "disabled1": "Subscription disabled",
    "disabled2": "The last payment has not been made.",
    "disabled3": "Manage payments",
    "canceled1": "Subscription canceled",
    "canceled2": "",
    "canceled3": "Get a new subscription",
    "1": "Your subscription",
    "2": "Your are admin of your team.",
    "3": "Your team has",
    "4": "CVs left, until the",
    "4bis": "by the end of the day.",
    "4ter": "Max monthly limit of CVs for your team: ",
    "4q": "(determined by the admin of your group).",
    "5": "Your current plan:",
    "6": "See all plans",
    "7": "Your team",
    "8": "Your team",
    "8bis": "has",
    "9": "user.",
    "9plural": "users.",
    "10": "Your team is linked to the domain name",
    "11": "Anyone creating an account with an email address having this domain name will be able to join your team.",
    "10plural": "Your team is linked to the domain names",
    "11plural": "Anyone creating an account with an email address having one of your domain names will be able to join your team.",
    "12": "For any request, please contact us at",
    "13": "None",
    "14": "Solo",
    "15": "Standard",
    "16": "Premium",
    "17": "Solo Yearly",
    "18": "Standard Yearly",
    "19": "Premium Yearly",
    "onlyVisibleByAdmins": "Only visible by admins",
    "onlyVisibleByAdminsAndManagers": "Visible by admins and managers",
    "onlyVisibleByParentCompanyAdmins": "Only visible by group admins",
    "usersDataTable1": "Email",
    "usersDataTable2": "First name",
    "usersDataTable3": "Last name",
    "usersDataTable5": "Number of CVs created over the last 30 days",
    "usersDataTable5bis": "Number of CVs created from beginning of current monthly cycle",
    "usersDataTable6": "Date of account creation",
    "usersDataTable7": "All",
    "usersDataTable8": "Number of users per page",
    "usersDataTable9": "{0}-{1} of {2}",
    "usersDataTable10": "Role",
    "role1": "Normal",
    "role2": "Manager",
    "role3": "Admin",
    "role4": "Disabled",

    "u1": "Edit the role of a user",
    "u2": "• Admins can edit the team's subscription.<br>• Admins can name Admins and Managers.<br>• Admins and Managers can invite/accept/refuse normal users in the team.",
    "u2bis": "Current role:",
    "u2ter": "New role",
    "u3": "Save",
    "u4": "Cancel",
    "u5": "Role is being saved...",

    "y": "Your role:",

    "z1": "Members of your team",
    "z2": "Parameters about joining your team",
    "z3": "New users must have an email address with a domain name linked to your team.",
    "z4": "New users must be validated",
    "z5": "Enabled",
    "z6": "Disabled",
    "z7": "New users will need to request account validation from a Manager or an Admin of your team.",
    "z8": "Pending validation requests",
    "z9": "No user is waiting for his account to be validated.",
    "z10": "These users are waiting for a validation of their account to join your team.",
    "z11": "Asked on",
    "z12": "Accept",
    "z13": "Refuse",
    "z14": "Pre-validated email addresses of co-workers",
    "z15": "No pre-validated email address.",
    "z15": "Aucune adresse email pré-validée.",
    "z16": "These users whitelisted by Managers and Admins can join your team by creating an account with this email address.",
    "z17": "Pre-validated on",
    "z18": "Cancel",
    "y1": "Limit the number of monthly usable CVs per user",
    "y2": "Limit",
    "y3": "enabled",
    "y4": "disabled",
    "y5": "Max number :",
    "y6": "CVs / user / month",
    "y7": "You will be able to enable this option when you have an active subscription.",
    "y8": "CV used by your personal account since the beginning of the monthly cycle.",
    "y8plural": "CVs used by your personal account since the beginning of the monthly cycle.",
    "y9": "Your team has set up a max number of",
    "y10": "CVs per user per month.",
    "y11": "Next refill on",

    "parentCompanyNoBillingDisplayed": "To update your subscription, please contact the admins who manage your team's subscription:",
    "defaultNewUserRole1": "Default role of new users",

    "disableUser1": "This user will not be able to login to his account and won't access the data of your team.",
    "disableUser2": "The CVs created by this user will remain, and keep their current visibility to the members of your team."
  }
}
</i18n>

<style scoped>
.v-tooltip__content {
  background-color: #37474f;
  opacity: 1 !important;
}
</style>